import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Mail, Save, Eye, Send, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function EmailTemplatesPage() {
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editSubject, setEditSubject] = useState('');
  const [editHtml, setEditHtml] = useState('');
  const [previewHtml, setPreviewHtml] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try { const r = await api.get('/admin/emails'); setTemplates(r.data); if (r.data.length > 0 && !selected) selectTemplate(r.data[0]); } catch {} finally { setLoading(false); }
  };

  const selectTemplate = (t) => { setSelected(t); setEditSubject(t.subject); setEditHtml(t.body_html); setShowPreview(false); };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try { await api.put(`/admin/emails/${selected.template_key}`, { subject: editSubject, body_html: editHtml }); toast.success('Template saved'); await loadTemplates(); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const handlePreview = async () => {
    if (!selected) return;
    try {
      const r = await api.post(`/admin/emails/${selected.template_key}/preview`, { template_key: selected.template_key, test_data: { customer_name: 'Jane Doe', order_number: 'TFD-20260101-TEST', item_title: 'Test Chanel Bag', magic_url: '/account/magic?token=test', refund_amount: '$40.00' } });
      setPreviewHtml(r.data.body_html); setShowPreview(true);
    } catch { toast.error('Preview failed'); }
  };

  const handleTestSend = async () => {
    if (!selected) return;
    try { const r = await api.post(`/admin/emails/${selected.template_key}/test-send`, {}); toast.success(r.data.message); } catch { toast.error('Send failed'); }
  };

  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>;

  return (
    <div data-testid="email-templates-page">
      <div className="flex items-center gap-2 mb-4"><Mail className="h-4 w-4 text-ink" /><h1 className="text-base font-semibold text-ink">Email Templates</h1><Badge variant="outline" className="text-[10px]">{templates.length} templates</Badge></div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        <Card className="bg-white border-stone/50">
          <CardContent className="p-2">
            {templates.map(t => (
              <button key={t.template_key} onClick={() => selectTemplate(t)} className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${selected?.template_key === t.template_key ? 'bg-secondary text-ink font-medium' : 'text-muted-foreground hover:text-ink hover:bg-secondary/60'}`} data-testid={`email-tmpl-${t.template_key}`}>
                {t.name}
              </button>
            ))}
          </CardContent>
        </Card>

        {selected ? (
          <Card className="bg-white border-stone/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div><h2 className="text-sm font-semibold text-ink">{selected.name}</h2><span className="text-[10px] font-mono text-muted-foreground">{selected.template_key}</span></div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handlePreview}><Eye className="h-3 w-3" /> Preview</Button>
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleTestSend}><Send className="h-3 w-3" /> Test Send</Button>
                  <Button size="sm" className="h-7 text-xs gap-1" onClick={handleSave} disabled={saving}><Save className="h-3 w-3" /> {saving ? 'Saving...' : 'Save'}</Button>
                </div>
              </div>
              <Separator className="mb-3" />
              <p className="text-[10px] text-muted-foreground mb-3">From: {selected.from_email} | Reply-To: {selected.reply_to || selected.from_email}</p>

              {showPreview ? (
                <div className="space-y-3">
                  <div className="p-3 rounded border border-stone/50 text-sm"><strong>Subject:</strong> {editSubject}</div>
                  <div className="p-4 rounded border border-stone/50 prose prose-sm max-w-none min-h-[200px]" style={{background:'hsl(var(--bg-paper))'}} dangerouslySetInnerHTML={{__html: previewHtml}} />
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowPreview(false)}>Back to Edit</Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <div><Label className="text-xs">Subject</Label><Input value={editSubject} onChange={e => setEditSubject(e.target.value)} className="mt-1 h-8 text-xs" /></div>
                  <div><Label className="text-xs">Body (HTML)</Label><textarea value={editHtml} onChange={e => setEditHtml(e.target.value)} className="w-full mt-1 p-3 rounded border border-stone text-xs font-mono min-h-[250px] resize-y focus:outline-none focus:ring-2 focus:ring-ring/30" style={{background:'hsl(var(--bg-paper))'}} /></div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : <Card className="bg-white border-stone/50"><CardContent className="p-8 text-center"><Mail className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" /><p className="text-sm text-muted-foreground">Select a template</p></CardContent></Card>}
      </div>
    </div>
  );
}
